// SPDX-License-Identifier: AGPL-3.0-or-later

// Profile badges drawn inline (bundled, no network round-trip, crisp at any size). Flat facets in a few tones,
// no outline, glow or shadow - the clean look of Discord's own badges.

import {AchievementBadgeArt} from '@app/features/user/achievements/AchievementBadgeArt';
import type React from 'react';
import {useId} from 'react';

interface BadgeIconProps {
	size?: number;
	className?: string;
	style?: React.CSSProperties;
}

/** First 5000 members: a glossy gold medallion with a star, framed by a laurel wreath and ribbon. */
export function FoundingMemberBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
	const gold = `fg${uid}`;
	const leaf = `fl${uid}`;
	const ribbon = `fr${uid}`;
	const shadow = `fs${uid}`;
	const small = size < 28;
	// One laurel branch (left); the right one is mirrored.
	const leaves = [
		[17, 47, -35],
		[12.5, 40.5, -55],
		[10.2, 32.5, -78],
		[11, 24.2, -100],
		[14.6, 16.8, -125],
	] as const;
	return (
		<svg width={size} height={size} viewBox="0 0 64 64" className={className} style={style} aria-hidden>
			<defs>
				<radialGradient id={gold} cx="0.35" cy="0.28" r="0.85">
					<stop offset="0" stopColor="#fff4c2" />
					<stop offset="0.45" stopColor="#ffc93d" />
					<stop offset="1" stopColor="#b9740a" />
				</radialGradient>
				<linearGradient id={leaf} x1="0" y1="0" x2="1" y2="1">
					<stop offset="0" stopColor="#ffe9a3" />
					<stop offset="1" stopColor="#d48f12" />
				</linearGradient>
				<linearGradient id={ribbon} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" stopColor="#ff7b8d" />
					<stop offset="1" stopColor="#c21f44" />
				</linearGradient>
				<filter id={shadow} x="-20%" y="-20%" width="140%" height="150%">
					<feDropShadow dx="0" dy={small ? 1 : 2} stdDeviation={small ? 0.7 : 1.6} floodColor="#5c3700" floodOpacity="0.5" />
				</filter>
			</defs>
			<g filter={`url(#${shadow})`}>
				<path d="M24 44 18 60l6-3 4 5 4-14Z" fill={`url(#${ribbon})`} />
				<path d="M40 44l6 16-6-3-4 5-4-14Z" fill={`url(#${ribbon})`} />
				{[1, -1].map((side) => (
					<g key={side} transform={side === -1 ? 'translate(64 0) scale(-1 1)' : undefined}>
						<path d="M20 50C11 44 7 34 9.5 23 11 17 14 13 17.5 10.5" stroke="#c98a12" strokeWidth="1.6" fill="none" strokeLinecap="round" />
						{leaves.map(([x, y, angle]) => (
							<ellipse key={`${x}-${y}`} cx={x} cy={y} rx="2.6" ry="5.2" fill={`url(#${leaf})`} transform={`rotate(${angle} ${x} ${y})`} />
						))}
					</g>
				))}
				<circle cx="32" cy="31" r="15" fill={`url(#${gold})`} />
				<circle cx="32" cy="31" r="15" fill="none" stroke="#fff3c4" strokeOpacity="0.8" strokeWidth="1.2" />
				<circle cx="32" cy="31" r="11.5" fill="none" stroke="#a8670a" strokeOpacity="0.45" strokeWidth="1" />
				<path
					d="m32 22.2 2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.8Z"
					fill="#fffaf0"
				/>
				<path d="m32 22.2 2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7Z" fill="#ffe7a8" />
				<path d="M22.5 24.5c1.6-3.4 4.4-5.6 8-6.4" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.75" />
			</g>
		</svg>
	);
}

/** Server boost: a flat faceted pink gem with a small sparkle. */
export function BoostGemBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" className={className} style={style} aria-hidden>
			<path d="M6 4h4l-1.6 5H2Z" fill="#ff9cfb" />
			<path d="M10 4h4l1.6 5H8.4Z" fill="#ffc4fd" />
			<path d="M14 4h4l4 5h-6.4Z" fill="#ff84fa" />
			<path d="M2 9h6.4L12 21Z" fill="#f25de9" />
			<path d="M8.4 9h7.2L12 21Z" fill="#ff78f8" />
			<path d="M15.6 9H22L12 21Z" fill="#d94bd3" />
			<path
				d="M19.6 1.4c.2 1.05.65 1.5 1.7 1.7-1.05.2-1.5.65-1.7 1.7-.2-1.05-.65-1.5-1.7-1.7 1.05-.2 1.5-.65 1.7-1.7Z"
				fill="#fff"
			/>
		</svg>
	);
}

/** Lafon Pro: the gold tier of the jewelled Pro crown used by the "Pro Journey" badge. */
export function ProBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	return (
		<span className={className} style={{display: 'inline-flex', ...style}}>
			<AchievementBadgeArt categoryId="pro" tierIndex={0} tierCount={9} size={size} />
		</span>
	);
}

/** Lafon Prime (lifetime): a glossy gold medallion with a crown, feathered wings and an amethyst star. */
export function PrimeBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
	const gold = `pg${uid}`;
	const wing = `pw${uid}`;
	const gem = `pa${uid}`;
	const shadow = `ps${uid}`;
	const blur = `pb${uid}`;
	const small = size < 28;
	const feathers = [
		[18, 24, 'M18 24c-6-4-12-4-16-1 5 1 9 3 12 6Z'],
		[17, 31, 'M17 31c-6-2-11-1-14 2 4 .4 8 1.6 11 4Z'],
		[18, 38, 'M18 38c-5 0-9 1.6-11 4.6 3.6-.2 7 .4 10 2Z'],
	] as const;
	return (
		<svg width={size} height={size} viewBox="0 0 64 64" className={className} style={style} aria-hidden>
			<defs>
				<radialGradient id={gold} cx="0.35" cy="0.28" r="0.85">
					<stop offset="0" stopColor="#fff6cf" />
					<stop offset="0.45" stopColor="#ffcd45" />
					<stop offset="1" stopColor="#b06c06" />
				</radialGradient>
				<linearGradient id={wing} x1="1" y1="0" x2="0" y2="1">
					<stop offset="0" stopColor="#fff3d8" />
					<stop offset="1" stopColor="#e3b25e" />
				</linearGradient>
				<radialGradient id={gem} cx="0.4" cy="0.3" r="0.8">
					<stop offset="0" stopColor="#efd9ff" />
					<stop offset="0.5" stopColor="#a463f2" />
					<stop offset="1" stopColor="#4d1d94" />
				</radialGradient>
				<filter id={shadow} x="-20%" y="-20%" width="140%" height="150%">
					<feDropShadow dx="0" dy={small ? 1 : 2.2} stdDeviation={small ? 0.8 : 1.8} floodColor="#5c3500" floodOpacity="0.5" />
				</filter>
				<filter id={blur} x="-30%" y="-30%" width="160%" height="160%">
					<feGaussianBlur stdDeviation="1.1" />
				</filter>
			</defs>
			<g filter={`url(#${shadow})`}>
				{[1, -1].map((side) => (
					<g key={side} transform={side === -1 ? 'translate(64 0) scale(-1 1)' : undefined}>
						{feathers.map(([x, y, d]) => (
							<path key={`${x}-${y}`} d={d} fill={`url(#${wing})`} stroke="#c8913a" strokeOpacity="0.5" strokeWidth="0.6" />
						))}
					</g>
				))}
				<path d="M21 20.5 24 9l5.2 5L32 5.5l2.8 8.5L40 9l3 11.5Z" fill={`url(#${gold})`} stroke="#fff1bf" strokeOpacity="0.7" strokeWidth="0.8" strokeLinejoin="round" />
				<circle cx="24" cy="9" r="1.8" fill="#ffffff" />
				<circle cx="32" cy="5.5" r="2" fill="#ffffff" />
				<circle cx="40" cy="9" r="1.8" fill="#ffffff" />
				<circle cx="32" cy="36" r="17" fill={`url(#${gold})`} />
				<circle cx="32" cy="36" r="17" fill="none" stroke="#fff3c4" strokeOpacity="0.85" strokeWidth="1.3" />
				<circle cx="32" cy="36" r="13" fill="#8a5304" opacity="0.25" />
				<circle cx="32" cy="36" r="12" fill={`url(#${gem})`} />
				<path
					d="M32 27.5c.9 4.4 3.2 6.7 7.6 7.6-4.4.9-6.7 3.2-7.6 7.6-.9-4.4-3.2-6.7-7.6-7.6 4.4-.9 6.7-3.2 7.6-7.6Z"
					fill="#ffffff"
				/>
				<path d="M21.5 29.5c1.8-4 5.2-6.6 9.4-7.4" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.8" filter={`url(#${blur})`} />
			</g>
		</svg>
	);
}

function useBadgeIds(prefix: string) {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
	return (name: string) => `${prefix}${name}${uid}`;
}

/** Bug hunter: a glossy emerald magnifier with a ruby beetle caught in its crosshair. */
export function BugHunterBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	const id = useBadgeIds('bh');
	const small = size < 28;
	return (
		<svg width={size} height={size} viewBox="0 0 64 64" className={className} style={style} aria-hidden>
			<defs>
				<radialGradient id={id('ring')} cx="0.32" cy="0.25" r="0.85">
					<stop offset="0" stopColor="#b4ffd6" />
					<stop offset="0.5" stopColor="#22c47a" />
					<stop offset="1" stopColor="#0a6b3f" />
				</radialGradient>
				<radialGradient id={id('glass')} cx="0.35" cy="0.3" r="0.8">
					<stop offset="0" stopColor="#f2fffa" stopOpacity="0.95" />
					<stop offset="0.6" stopColor="#bdf0dc" stopOpacity="0.55" />
					<stop offset="1" stopColor="#6fc9a6" stopOpacity="0.6" />
				</radialGradient>
				<radialGradient id={id('bug')} cx="0.35" cy="0.3" r="0.8">
					<stop offset="0" stopColor="#ff9aa9" />
					<stop offset="0.55" stopColor="#e8284e" />
					<stop offset="1" stopColor="#8f0d2b" />
				</radialGradient>
				<filter id={id('shadow')} x="-20%" y="-20%" width="140%" height="150%">
					<feDropShadow dx="0" dy={small ? 1 : 2.2} stdDeviation={small ? 0.8 : 1.8} floodColor="#053b22" floodOpacity="0.55" />
				</filter>
			</defs>
			<g filter={`url(#${id('shadow')})`}>
				<path d="M40.5 40.5 55 55" stroke="#0a6b3f" strokeWidth="9.5" strokeLinecap="round" />
				<path d="M40.5 40.5 55 55" stroke={`url(#${id('ring')})`} strokeWidth="7" strokeLinecap="round" />
				<path d="M44 44.5 52 52.5" stroke="#ffffff" strokeOpacity="0.5" strokeWidth="1.6" strokeLinecap="round" />
				<circle cx="27" cy="27" r="20" fill={`url(#${id('ring')})`} />
				<circle cx="27" cy="27" r="15" fill={`url(#${id('glass')})`} />
				<path d="M27 14.5v5M27 34.5v5M14.5 27h5M34.5 27h5" stroke="#0a6b3f" strokeOpacity="0.55" strokeWidth="1.4" strokeLinecap="round" />
				<g stroke="#4f0515" strokeWidth="1.3" strokeLinecap="round">
					<path d="M22 24.5l-3-1.6M22 28.5h-3.4M22.6 32l-2.8 1.8M32 24.5l3-1.6M32 28.5h3.4M31.4 32l2.8 1.8" />
				</g>
				<ellipse cx="27" cy="29" rx="5.4" ry="6.4" fill={`url(#${id('bug')})`} />
				<path d="M27 22.6v12.8" stroke="#4f0515" strokeOpacity="0.7" strokeWidth="1" />
				<circle cx="27" cy="21.6" r="2.6" fill="#3a0a16" />
				<circle cx="24.8" cy="28" r="1" fill="#3a0a16" />
				<circle cx="29.4" cy="31" r="1" fill="#3a0a16" />
				<path d="M15.5 21c1.6-4 5-6.8 9.4-7.6" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.9" />
				<circle cx="27" cy="27" r="20" fill="none" stroke="#d5ffe9" strokeOpacity="0.6" strokeWidth="1.2" />
			</g>
		</svg>
	);
}

/** Partner: a glossy sapphire-to-violet diamond tile with a bright four-point star. */
export function PartnerBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	const id = useBadgeIds('pt');
	const small = size < 28;
	const tile = 'M32 4.5a5 5 0 0 1 3.5 1.5l22.5 22.5a5 5 0 0 1 0 7L35.5 58a5 5 0 0 1-7 0L6 35.5a5 5 0 0 1 0-7L28.5 6a5 5 0 0 1 3.5-1.5Z';
	const face = 'M32 11a3.4 3.4 0 0 1 2.4 1l16.6 16.6a3.4 3.4 0 0 1 0 4.8L34.4 50a3.4 3.4 0 0 1-4.8 0L13 33.4a3.4 3.4 0 0 1 0-4.8L29.6 12a3.4 3.4 0 0 1 2.4-1Z';
	return (
		<svg width={size} height={size} viewBox="0 0 64 64" className={className} style={style} aria-hidden>
			<defs>
				<linearGradient id={id('edge')} x1="0" y1="0" x2="1" y2="1">
					<stop offset="0" stopColor="#9fd0ff" />
					<stop offset="1" stopColor="#3a1f9e" />
				</linearGradient>
				<radialGradient id={id('face')} cx="0.32" cy="0.22" r="0.95">
					<stop offset="0" stopColor="#8ec9ff" />
					<stop offset="0.5" stopColor="#4a74f0" />
					<stop offset="1" stopColor="#6a3de0" />
				</radialGradient>
				<linearGradient id={id('gloss')} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" stopColor="#ffffff" stopOpacity="0.55" />
					<stop offset="1" stopColor="#ffffff" stopOpacity="0" />
				</linearGradient>
				<filter id={id('shadow')} x="-20%" y="-20%" width="140%" height="150%">
					<feDropShadow dx="0" dy={small ? 1 : 2.2} stdDeviation={small ? 0.8 : 1.8} floodColor="#1d1260" floodOpacity="0.55" />
				</filter>
			</defs>
			<g filter={`url(#${id('shadow')})`}>
				<path d={tile} fill={`url(#${id('edge')})`} />
				<path d={face} fill={`url(#${id('face')})`} />
				<path d="M32 11a3.4 3.4 0 0 1 2.4 1L51 28.6a3.4 3.4 0 0 1 .9 1.6H12.1a3.4 3.4 0 0 1 .9-1.6L29.6 12a3.4 3.4 0 0 1 2.4-1Z" fill={`url(#${id('gloss')})`} />
				<path
					d="M32 18.5c1.2 6.4 4.6 9.8 11 11-6.4 1.2-9.8 4.6-11 11-1.2-6.4-4.6-9.8-11-11 6.4-1.2 9.8-4.6 11-11Z"
					fill="#ffffff"
				/>
				<path d={tile} fill="none" stroke="#d8ecff" strokeOpacity="0.55" strokeWidth="1" />
			</g>
		</svg>
	);
}
